import { useAtom } from 'jotai';
import { X } from 'lucide-react';
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react';

import { perfDebugLogAtom, type PerfDebugLogEntry } from './debug-log';

const TYPE_COLORS: Record<PerfDebugLogEntry['type'], string> = {
  start: 'text-skies-500',
  checkpoint: 'text-sunlight-500',
  finish: 'text-forest-500',
  cancel: 'text-bark-400',
  abandoned: 'text-tomato-500',
};

/**
 * Drag logic lives entirely in refs + DOM mutations — zero React re-renders.
 * Returns a pointerDown handler to attach to the drag handle.
 */
function useDrag(panelRef: React.RefObject<HTMLDivElement | null>) {
  const dragState = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      const el = panelRef.current;
      if (!el) return;

      // Initialise position from current computed style if not yet set
      if (!el.style.left) {
        const rect = el.getBoundingClientRect();
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;
        el.style.right = 'auto';
        el.style.bottom = 'auto';
      }

      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: parseInt(el.style.left, 10),
        origY: parseInt(el.style.top, 10),
      };

      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [panelRef]
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      const state = dragState.current;
      const el = panelRef.current;
      if (!state || !el) return;

      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      el.style.left = `${state.origX + dx}px`;
      el.style.top = `${state.origY + dy}px`;
    },
    [panelRef]
  );

  const onPointerUp = useCallback(() => {
    dragState.current = null;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp };
}

export function PerfDebugPanel() {
  const [logs, setLogs] = useAtom(perfDebugLogAtom);
  const [isOpen, setIsOpen] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const { onPointerDown, onPointerMove, onPointerUp } = useDrag(panelRef);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-999 rounded-md bg-bark-900 px-2 py-1 font-mono text-xs text-white opacity-80"
      >
        Perf ({logs.length})
      </button>
    );
  }

  return (
    <div
      ref={panelRef}
      className="fixed bottom-4 right-4 z-999 flex max-h-80 w-96 flex-col rounded-lg border border-bark-600 bg-bark-900 font-mono text-xs text-white shadow-xl"
    >
      {/* Drag handle — only this bar initiates drag */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="flex cursor-grab select-none items-center justify-between border-b border-bark-600 px-3 py-2 active:cursor-grabbing"
      >
        <span className="font-semibold">Perf Tracking Debug</span>
        <div className="flex gap-2">
          <button onClick={() => setLogs([])} className="text-bark-400 hover:text-white">
            Clear
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="text-bark-400 hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {logs.length === 0 ? (
          <div className="py-4 text-center text-bark-400">No events yet</div>
        ) : (
          logs.map((entry, i) => (
            <div
              key={i}
              className="flex gap-2 border-b border-bark-800 py-1.5 last:border-0"
            >
              <span className={TYPE_COLORS[entry.type]}>
                {entry.type.toUpperCase().padEnd(10)}
              </span>
              <span className="flex-1 truncate text-bark-200">
                {entry.key} {entry.detail ? `: ${entry.detail}` : ''}
              </span>
              {entry.elapsedMs != null && (
                <span className="text-bark-400">{entry.elapsedMs.toFixed(0)}ms</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
